import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CacheKeys } from '@/lib/cache'
import { parseError } from '@/lib/errors'
import {
  addUserToOrg,
  getOrgRoleLabel,
  type OrgMember,
  OrgRole,
  removeUserFromOrg,
  useOrganisations,
  useUserOrgs,
} from '@/models/user'

const MIN_SEARCH_LENGTH = 2

const ROLE_OPTIONS: { value: OrgRole; label: string }[] = [
  { value: OrgRole.USER, label: 'User' },
  { value: OrgRole.ADMIN, label: 'Admin' },
  { value: OrgRole.BUSINESS_OWNER, label: 'Business Owner' },
]

/**
 * Manage which orgs a user has access to. A user can belong to several orgs but
 * never to none, so the last membership can't be removed.
 */
export function ManageUserOrgsDialog({
  member,
  open,
  onOpenChange,
}: {
  member: OrgMember
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [role, setRole] = useState<OrgRole>(OrgRole.USER)
  const queryClient = useQueryClient()

  const { memberships, isLoading } = useUserOrgs(member.id, open)

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query), 250)
    return () => clearTimeout(timeout)
  }, [query])

  const searchEnabled = debouncedQuery.trim().length >= MIN_SEARCH_LENGTH
  const { organisations, isLoading: isSearching } = useOrganisations({
    enabled: open && searchEnabled,
    search: searchEnabled ? debouncedQuery.trim() : undefined,
  })

  const memberOrgIds = new Set(memberships.map((membership) => membership.orgId))
  const candidates = organisations.filter((org) => !memberOrgIds.has(org.id))
  const selectedOrg = organisations.find((org) => org.id === selectedOrgId)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.USER_ORGANISATIONS, member.id] })
    queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS] })
    queryClient.invalidateQueries({ queryKey: [CacheKeys.CURRENT_USER] })
  }

  const addMutation = useMutation({
    mutationFn: (orgId: string) => addUserToOrg(member.id, orgId, role),
    onSuccess: () => {
      toast.success('Access granted')
      setQuery('')
      setDebouncedQuery('')
      setSelectedOrgId(null)
      setRole(OrgRole.USER)
      invalidate()
    },
    onError: (error) => toast.error(parseError(error)),
  })

  const removeMutation = useMutation({
    mutationFn: (orgId: string) => removeUserFromOrg(member.id, orgId),
    onSuccess: () => {
      toast.success('Access removed')
      invalidate()
    },
    onError: (error) => toast.error(parseError(error)),
  })

  const isLastOrg = memberships.length <= 1

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Organization access</DialogTitle>
          <DialogDescription>
            Organizations {member.firstName} {member.lastName} ({member.email}) can access.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-2">
          {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
          {!isLoading && memberships.length === 0 && (
            <p className="text-muted-foreground text-sm">This user has no organizations.</p>
          )}
          {memberships.map((membership) => (
            <div
              key={membership.id}
              className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Building2 className="size-4 shrink-0 text-neutral-400" />
                <span className="truncate font-medium">{membership.orgName}</span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-muted-foreground text-sm">{getOrgRoleLabel(membership.role)}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={isLastOrg || removeMutation.isPending}
                        onClick={() => removeMutation.mutate(membership.orgId)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isLastOrg ? 'A user must belong to at least one organization' : 'Remove access'}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <Label>Add to organization</Label>
          <Command shouldFilter={false} className="rounded-md border">
            <CommandInput
              placeholder="Search organizations…"
              value={query}
              onValueChange={(value) => {
                setQuery(value)
                setSelectedOrgId(null)
              }}
            />
            {searchEnabled && !selectedOrgId && (
              <CommandList className="max-h-48">
                <CommandEmpty>{isSearching ? 'Searching…' : 'No organizations found'}</CommandEmpty>
                {candidates.map((org) => (
                  <CommandItem
                    key={org.id}
                    value={org.id}
                    onSelect={() => {
                      setSelectedOrgId(org.id)
                      setQuery(org.name)
                    }}
                  >
                    <span className="truncate">{org.name}</span>
                    {org.business?.dotNumber && (
                      <span className="text-muted-foreground ml-auto text-xs">
                        DOT {org.business.dotNumber}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandList>
            )}
          </Command>

          <div className="mt-2 flex flex-col gap-2">
            <Label htmlFor="user-org-role">Role</Label>
            <Select value={role} onValueChange={(value) => setRole(value as OrgRole)}>
              <SelectTrigger id="user-org-role" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            disabled={!selectedOrgId || addMutation.isPending}
            loading={addMutation.isPending ? 'Adding…' : undefined}
            onClick={() => selectedOrgId && addMutation.mutate(selectedOrgId)}
          >
            {selectedOrg ? `Add to ${selectedOrg.name}` : 'Add to organization'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
